import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';

// The number text alerts are sent from. Leave '' to show the generic
// wording; set it (e.g. '(808) 555-0123') and it appears in the "texts"
// answer so customers can save it as a contact.
const SMS_FROM = '(808) 300-5604';

// Physical install / sensor mounting is intentionally NOT covered here yet —
// that lives in the printed guide and will be added later. This page is a
// plain-language "how the app works" for non-technical customers: short,
// scannable, tap-to-open answers, each linking to where the thing is done.

const CHEVRON = (
  <svg className="help-faq__chev" width="18" height="18" viewBox="0 0 24 24" fill="none"
       stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export default function Help() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(() => new Set());

  const toggle = (id) => {
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Small "go do it" link used inside answers.
  const Go = ({ to, children }) => (
    <button type="button" className="help-faq__link" onClick={() => navigate(to)}>
      {children} <span aria-hidden="true">→</span>
    </button>
  );

  // FAQ content, grouped. `a` is the answer body (JSX).
  const SECTIONS = [
    {
      label: 'Your tanks',
      items: [
        {
          id: 'setup',
          q: 'How do I set up a tank?',
          a: (
            <>
              <p>Two things tell the app what your readings mean: a quick sensor calibration, and your tank's
                <strong> Empty &amp; Full depths</strong> (what counts as 0% and 100%) plus its total gallons. The app
                walks you through it the first time, and you can change any of it later.</p>
              <Go to="/settings">Open Settings → Tank</Go>
            </>
          ),
        },
        {
          id: 'recal',
          q: 'Do I ever need to recalibrate?',
          a: (
            <>
              <p><strong>Almost never.</strong> Once your empty and full points are set, they stay put. You'd only
                recalibrate if you move the sensor or the readings clearly drift over time.</p>
            </>
          ),
        },
      ],
    },
    {
      label: 'Alerts',
      items: [
        {
          id: 'get-alerts',
          q: 'How do I get alerts?',
          a: (
            <>
              <p>On the Alerts page, it's two steps: first turn on <strong>how to reach you</strong> (email, push,
                or text), then choose <strong>when to alert you</strong> (a low level, a custom %, full, or if the
                device goes offline). Turning on email, push, or text without also picking a level won't send
                anything — pick at least one.</p>
              <Go to="/alerts">Open Alerts</Go>
            </>
          ),
        },
        {
          id: 'levels',
          q: 'What types of alerts are there?',
          a: (
            <>
              <p><strong>Low water</strong> — when the tank drops below a level you pick (e.g. 20%), or your own
                custom %.<br />
                <strong>Full</strong> — when it's topped up again.<br />
                <strong>Device offline</strong> — a heads-up if the unit hasn't reported in over 24 hours, so you can
                check its power or Wi-Fi.</p>
            </>
          ),
        },
        {
          id: 'email-spam',
          q: "I'm not getting email alerts",
          a: (
            <>
              <p>Check your <strong>spam / junk</strong> folder first. Alerts come from
                {' '}<strong>alerts@sokowatersystems.com</strong> — add that address to your contacts so future
                alerts land in your inbox.</p>
            </>
          ),
        },
        {
          id: 'sms-number',
          q: 'What number do text alerts come from?',
          a: (
            <>
              <p>Texts arrive from a dedicated SoKo Water Systems number{SMS_FROM ? <> (<strong>{SMS_FROM}</strong>)</> : ''}.
                Save it as a contact when the first one arrives so it's easy to recognize. You can reply
                <strong> STOP</strong> anytime to turn texts off, or <strong>HELP</strong> for help.</p>
            </>
          ),
        },
      ],
    },
    {
      label: 'Sharing',
      items: [
        {
          id: 'sharing',
          q: 'What is sharing?',
          a: (
            <>
              <p>Sharing lets family or a helper keep an eye on your tanks. Enter their email and they get access
                with their own login — they can see your dashboard and set up their <em>own</em> alerts. You stay
                the owner, and you can revoke access at any time. There's no extra account fee.</p>
              <Go to="/settings?tab=sharing">Open Settings → Sharing</Go>
            </>
          ),
        },
      ],
    },
    {
      label: 'Preferences',
      items: [
        {
          id: 'prefs',
          q: 'Change units or light / dark mode',
          a: (
            <>
              <p>In Preferences you can show depth as feet-and-inches or a decimal, and switch the app between
                light and dark mode.</p>
              <Go to="/settings?tab=preferences">Open Settings → Preferences</Go>
            </>
          ),
        },
        {
          id: 'led',
          q: 'The light bar on the unit',
          a: (
            <>
              <p>The LED bar on the device shows your tank level at a glance. You can set it to stay
                <strong> always on</strong>, light up <strong>for 30 seconds after a button press</strong>, or
                <strong> wake on changes</strong> (during a fill or a leak). It's under Preferences.</p>
              <Go to="/settings?tab=preferences">Open Settings → Preferences</Go>
            </>
          ),
        },
      ],
    },
    {
      label: 'Trends & devices',
      items: [
        {
          id: 'trends',
          q: 'Reading the Trends graph',
          a: (
            <>
              <p>Trends shows your water level over time. Pick a range — from the last 12 hours up to 6 months —
                and tap anywhere on the chart to see the exact level at that moment.</p>
              <Go to="/trends">Open Trends</Go>
            </>
          ),
        },
        {
          id: 'add-remove',
          q: 'Add or remove a device',
          a: (
            <>
              <p>In Settings, tap <strong>"+ Add/remove device."</strong> To add one you'll need the device ID and
                claim code from the card in your box. Removing a device you own also removes it for anyone you've
                shared it with.</p>
              <Go to="/settings">Open Settings</Go>
            </>
          ),
        },
      ],
    },
  ];

  return (
    <div className="app-container">
      <TopBar subtitle="Help" />
      <div className="page-content">

        <div className="help-head">
          <div>
            <div className="eyebrow">How-to</div>
            <h1>Help</h1>
          </div>
          <button type="button" className="help-back" onClick={() => navigate(-1)}>← Back</button>
        </div>

        <p className="help-intro">
          Quick answers about using the app. Tap a question to open it.
        </p>

        {/* Guided walkthrough launcher — jumps to the dashboard and replays the
            interactive tour of the app's main areas. */}
        <button
          type="button"
          className="help-tour-cta"
          onClick={() => navigate('/dashboard?tour=1')}
        >
          <span className="help-tour-cta__icon" aria-hidden="true">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 8v4l3 2" />
            </svg>
          </span>
          <span className="help-tour-cta__text">
            <span className="help-tour-cta__title">Take the app tour</span>
            <span className="help-tour-cta__sub">A quick guided walkthrough of the dashboard</span>
          </span>
          <span className="help-tour-cta__arrow" aria-hidden="true">→</span>
        </button>

        {SECTIONS.map((section) => (
          <div key={section.label} className="help-section">
            <div className="section-label">{section.label}</div>
            <div className="help-faq">
              {section.items.map((item) => {
                const isOpen = open.has(item.id);
                return (
                  <div key={item.id} className={`help-faq__item ${isOpen ? 'is-open' : ''}`}>
                    <button
                      type="button"
                      className="help-faq__q"
                      aria-expanded={isOpen}
                      onClick={() => toggle(item.id)}
                    >
                      <span>{item.q}</span>
                      {CHEVRON}
                    </button>
                    {isOpen && <div className="help-faq__a">{item.a}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <p className="help-support">
          Still stuck? Email <a href="mailto:support@sokowatersystems.com">support@sokowatersystems.com</a>
          {' '}or call or text <a href="tel:+18087474089">(808) 747-4089</a>
          {' '}— real humans, Hawai&#699;i hours.
        </p>

      </div>
      <BottomNav />
    </div>
  );
}
